<template>
  <div class="h-screen flex flex-col bg-pitch-black text-total-white">
    <!-- Header Section -->
    <TopNavbar />

    <!-- Project Selection -->
    <div class="p-4 flex-1 flex max-w-4xl mx-auto w-full flex-col">
      <!-- Title -->
      <div class="py-2 flex justify-between items-start w-full">
        <button @click="goBack" class="text-prime-golden text-2xl">
          <i class="fa fa-chevron-left"></i>
        </button>
        <div class="flex flex-col flex-1">
          <!--<h2 class="text-2xl font-semibold">
            Assign Projects to 
          </h2>-->
          <h2 class="text-2xl font-semibold pl-4">
            {{ contact?.first_name || "..." }} {{ contact?.last_name || "..." }}'s
            Projects
          </h2>
        </div>
      </div>

      <!-- Searchable Dropdown for Existing Projects -->
      <div class="my-4">
        <!-- Dropdown Input for Selecting Projects -->
        <div class="flex justify-between items-center gap-4">
          <!--<button v-if="!dropdownOpen" @click="createNewProject">
            <i class="fa fa-plus text-prime-golden text-2xl"></i>
          </button>-->
          <div
            :class="[
              'flex flex-1 relative rounded-md items-center border border-dark-grey gap-1',
              dropdownOpen ? 'bg-dark-grey py-4' : 'bg-eerie-black py-2',
            ]"
          >
            <div class="w-full relative bg-transparent px-2 flex items-center">
              <!-- When dropdown is closed, show placeholder -->
              <div
                v-if="!dropdownOpen"
                @click="toggleDropdown"
                class="w-full flex justify-between items-center cursor-pointer"
              >
                <span class="text-sm font-semibold text-total-white p-1">
                  {{ "Assign new project" }}
                </span>
                <i class="fas fa-chevron-down text-prime-golden mr-1"></i>
              </div>

              <!-- When dropdown is open, show search input -->
              <div v-else class="w-full flex items-center px-2">
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="Search projects..."
                  class="w-full bg-transparent border-none text-total-white outline-none"
                />
                <button
                  @click="closeDropdown"
                  class="text-xs text-pitch-black px-1 rounded-full bg-ash-grey ml-2"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>

              <!-- Dropdown List -->
              <div
                v-if="dropdownOpen"
                class="absolute top-10 mt-1 w-full left-0 bg-eerie-black border border-dark-grey rounded-md z-10"
              >
                <!-- No projects available to add message -->
                <div
                  v-if="filteredProjects.length === 0"
                  class="p-4 text-center text-gray-500"
                >
                  No projects available to assign.
                </div>
                <!-- Project List -->
                <ul v-else>
                  <!--<li
                    @mousedown.prevent="createNewProject"
                    class="p-2 hover:bg-dark-grey cursor-pointer flex items-center"
                  >
                    <span class="text-prime-golden">+ New Project</span>
                  </li>-->
                  <li
                    v-for="project in filteredProjects"
                    :key="project.id"
                    @mousedown.prevent="selectProject(project)"
                    class="p-4 hover:bg-dark-grey cursor-pointer flex items-center"
                  >
                    <span>{{ project.address_1 }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <!-- Tags for Selected Projects -->
        <div class="flex flex-wrap gap-2 mb-4 mt-4">
          <div
            v-for="project in selectedProjects"
            :key="project.id"
            class="flex items-center bg-light-grey-translucent-500 text-white py-2 px-3 rounded-lg"
          >
            <span>{{ project.address_1 }}</span>
            <button
              @click="removeProject(project)"
              class="ml-2 text-xs text-pitch-black rounded-full"
            >
              <!--<i class="fas fa-times text-ash-grey text-lg"></i>-->
              <img src="@/assets/images/trash-icon.svg" class="text-prime-golden" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Assign Projects Button -->
    <!--<div class="p-4 max-w-4xl mx-auto mb-4">
      <button
        @click="assignProjectsToContact"
        :disabled="selectedProjects.length === 0"
        class="w-full py-2 px-4 rounded-lg text-sm font-semibold bg-prime-golden text-pitch-black disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Assign Projects to Contact
      </button>
    </div>-->
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useProjectStore } from "@/stores/project";
import { useContactsStore } from "@/stores/contact";
import TopNavbar from "@/components/TopNavbar.vue";

const router = useRouter();
const route = useRoute();
const projectStore = useProjectStore();
const contactStore = useContactsStore();
const contact = ref({});
const projects = ref([]);
const searchQuery = ref("");
const dropdownOpen = ref(false);
const selectedProjects = ref(contact.value?.projects || []);

onMounted(async () => {
  await fetchAllProjects();
  await contactStore.fetchContactById(route.params.id);
  contact.value = contactStore.currentContact;
  selectedProjects.value = contact.value?.projects || [];
});

const fetchAllProjects = async () => {
  try {
    await projectStore.fetchAllProjects();
    projects.value = projectStore.projects;
  } catch (err) {
    return err;
  }
};

const toggleDropdown = () => {
  dropdownOpen.value = true;
  searchQuery.value = "";
};

const selectProject = async (project) => {
  await contactStore.addStakeholdersToProject(project.id, [contact.value.id]);
  selectedProjects.value.push(project);
  dropdownOpen.value = false;
  searchQuery.value = "";
};

const removeProject = async (project) => {
  await contactStore.deleteContactFromProject(project.id, contact.value.id);
  selectedProjects.value = selectedProjects.value.filter((p) => p.id !== project.id);
};

const closeDropdown = () => {
  dropdownOpen.value = false;
  searchQuery.value = "";
};

const filteredProjects = computed(() => {
  return projects.value.filter(
    (project) =>
      project.address_1.toLowerCase().includes(searchQuery.value.toLowerCase()) &&
      !selectedProjects.value.some((selected) => selected.id === project.id) &&
      !contact.value?.projects?.some((contactProject) => contactProject.id === project.id)
  );
});

// const createNewProject = () => {
//   projectStore.setProjectsToAdd(selectedProjects.value, route.params.id);
//   router.push({ name: "CreateProjectPage", query: { from_contact_add_project: route.params.id } });
// };

// const assignProjectsToContact = async () => {
//   if (selectedProjects.value.length > 0) {
//     const projectIds = selectedProjects.value.map((project) => project.id);

//     try {
//       await Promise.all(
//         projectIds.map((projectId) =>
//           contactStore.addStakeholdersToProject(projectId, [route.params.id])
//         )
//       );

//       // Clear the selected projects after successful assignment
//       selectedProjects.value = [];

//       // Navigate back to the Contact page with the Projects tab open
//       router.push({
//         name: "ContactViewPage",
//         params: { id: route.params.id },
//       });
//     } catch (error) {
//       console.error("Error assigning projects to contact:", error);
//     }
//   }
// };

const goBack = () => {
  //projectStore.setProjectsToAdd(selectedProjects.value, route.params.id);
  router.push({
    name: "ContactViewPage",
    params: { id: route.params.id },
    query: { projectId: route.params.projectId },
  });
};
</script>

<style scoped>
/* You can add your custom styles here to improve the UI/UX */
</style>
